import { useEffect } from 'react';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import Services from './components/Services';
import HowItWorks from './components/HowItWorks';
import Packages from './components/Packages';
import EditingForm from './components/EditingForm';
import FAQ from './components/FAQ';
import Footer from './components/Footer';
import { SchemaOrg } from '../../utils/seo';

export default function VideoEditingPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Short-Form Video Editing Services Long Island | Bellport Branding';

    const setMeta = (name: string, content: string, property?: boolean) => {
      const attr = property ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', 'Professional short-form video editing for creators and businesses on Long Island. Send your footage via Google Drive or Dropbox — we handle cuts, music, captions, voiceovers & more. 48-hour delivery. Serving Nassau & Suffolk County.');
    setMeta('keywords', 'video editing Long Island, short form video editing, social media video editing, reels editing, TikTok video editing, YouTube video editing, Bellport NY, Nassau County, Suffolk County');
    setMeta('geo.region', 'US-NY');
    setMeta('geo.placename', 'Bellport, New York');
    setMeta('geo.position', '40.7567;-72.9390');
    setMeta('ICBM', '40.7567, -72.9390');
    setMeta('og:title', 'Short-Form Video Editing Services Long Island | Bellport Branding', true);
    setMeta('og:description', 'Professional short-form video editing for Long Island creators and businesses. 48-hour delivery. Cuts, music, captions, voiceovers & more.', true);
    setMeta('og:type', 'website', true);
    setMeta('og:url', 'https://www.bellportbranding.com/video-editing', true);
    setMeta('og:image', 'https://www.bellportbranding.com/og-video-editing.jpg', true);
    setMeta('og:locale', 'en_US', true);
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:url', 'https://www.bellportbranding.com/video-editing');
    setMeta('twitter:title', 'Short-Form Video Editing Services Long Island | Bellport Branding');
    setMeta('twitter:description', 'Professional short-form video editing for Long Island creators and businesses. 48-hour delivery.');
    setMeta('twitter:image', 'https://www.bellportbranding.com/og-video-editing.jpg');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://www.bellportbranding.com/video-editing');
  }, []);

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        "name": "Short-Form Video Editing Services",
        "description": "Professional short-form video editing for creators and businesses. Cuts, music, captions, voiceovers, and more with 48-hour delivery.",
        "provider": {
          "@type": "LocalBusiness",
          "name": "Bellport Branding",
          "telephone": "(631) 640-7888",
          "email": "info@bellportbranding.com",
          "url": "https://www.bellportbranding.com",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Bellport",
            "addressRegion": "NY",
            "addressCountry": "US"
          }
        },
        "areaServed": {
          "@type": "Place",
          "name": "Long Island, New York"
        }
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "How do I send my footage?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "You can send your footage via Google Drive or Dropbox. Simply upload your files and share the link with us."
            }
          },
          {
            "@type": "Question",
            "name": "How fast is the turnaround?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Our standard turnaround is 48 hours. Rush delivery options are also available."
            }
          }
        ]
      }
    ]
  };

  return (
    <>
      <SchemaOrg type="Service" data={structuredData} />
      <div className="min-h-screen bg-white">
        <Navbar />
        <main>
          <Hero />
          <Services />
          <HowItWorks />
          <Packages />
          <EditingForm />
          <FAQ />
        </main>
        <Footer />
      </div>
    </>
  );
}